// This pragma is required for the css utility to work
import * as React from 'react';

/** @jsx jsx */
import { css, type SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { type Property } from 'csstype';

import { colors, fontWeight } from '~/styles/constants';
import { type CSSLengthUnit } from '~/utils/css';

export type VerticalAlignment =
  | 'baseline'
  | 'bottom'
  | 'inherit'
  | 'initial'
  | 'middle'
  | 'sub'
  | 'super'
  | 'text-bottom'
  | 'text-top'
  | 'top'
  | CSSLengthUnit;

interface CellCommonProps {
  asGrid?: boolean;
  colSpan?: number;
  cursor?: Property.Cursor;
  customStyles?: SerializedStyles;
  isHeader?: boolean;
  minWidth?: string;
  noLeftPadding?: boolean;
  noPadding?: boolean;
  noRightPadding?: boolean;
  rowIndex?: number;
  verticalAlign?: VerticalAlignment;
  width?: string;
}

interface InnerCellProps extends CellCommonProps {
  onClick?: (evt: React.MouseEvent<HTMLTableCellElement, MouseEvent>) => void;
}
export interface CellProps extends CellCommonProps {
  children?: React.ReactNode;
  onClick?: (rowIndex: number) => void;
}

const cellStyles = css`
  display: table-cell;
  height: inherit;
  padding: 15px 15px;
  vertical-align: middle;
  font-size: 13px;
  line-height: 18px;
  font-weight: ${fontWeight.normal};

  :first-of-type {
    padding-left: 10px;
  }

  :last-of-type {
    padding-right: 10px;
  }
`;

const Cell = styled('td')<InnerCellProps>`
  ${cellStyles};
  ${(props) =>
    props.cursor ? `cursor: ${props.cursor};` : props.onClick ? 'cursor: pointer;' : ''}

  color: ${colors.gray700};

  ${(props) => (props.noLeftPadding ? 'padding-left: 0;' : '')}
  ${(props) => (props.noRightPadding ? 'padding-right: 0;' : '')}
  ${({ noPadding }) => (noPadding ? 'padding: 0;' : '')}
  ${(props) => (props.verticalAlign ? `vertical-align: ${props.verticalAlign};` : '')}
  ${(props) => props.customStyles};
`;

const Header = styled('th')<InnerCellProps>`
  ${cellStyles};
  color: ${colors.secondary};
  font-size: 12px;
  font-weight: 400;
  width: ${(props) => props.width || 'inherit'};
  ${(props) => (props.minWidth ? `min-width: ${props.minWidth};` : '')}
  text-align: left;

  ${(props) => (props.noLeftPadding ? 'padding-left: 0;' : '')}
  ${(props) => (props.noRightPadding ? 'padding-right: 0;' : '')}
  ${(props) => props.customStyles}
`;

const TableCell = (props: CellProps) => {
  const {
    asGrid,
    children,
    colSpan,
    cursor,
    customStyles,
    isHeader,
    noLeftPadding,
    noPadding,
    noRightPadding,
    onClick,
    rowIndex,
    verticalAlign,
    width,
  } = props;

  const onClickCallback = React.useCallback(
    (evt) => {
      if (onClick && rowIndex !== undefined) {
        evt.stopPropagation();
        return onClick(rowIndex);
      }
    },
    [onClick, rowIndex],
  );

  const CellComponent = isHeader ? Header : Cell;

  return (
    <CellComponent
      asGrid={asGrid}
      colSpan={colSpan}
      cursor={cursor}
      customStyles={customStyles}
      isHeader={isHeader}
      noLeftPadding={noLeftPadding}
      noPadding={noPadding}
      noRightPadding={noRightPadding}
      onClick={onClick ? onClickCallback : undefined}
      verticalAlign={verticalAlign}
      width={width}
    >
      {children}
    </CellComponent>
  );
};

const ComposedTableCell = React.memo(TableCell);

export default ComposedTableCell as React.ComponentType<CellProps>;
